import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { NavLink, useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
// Phase 5: Iconoir — distinctive rounded European design
import {
  ViewGrid as DashboardIcon,
  Group as ClientsIcon,
  BookStack as PlaybooksIcon,
  UserScan as LeadDiscoveryIcon,

  Folder as CampaignsIcon,
  Mail as EmailIcon,
  Prohibition as BlockListIcon,
  ChatBubbleQuestion as RepliesIcon,
  StatsUpSquare as AnalyticsIcon,
  DollarCircle as CostsIcon,
  Page as ReportsIcon,
  Globe as StudioIcon,
  AppWindow as PortalIcon,
  Activity as ActivityIcon,
  SystemShut as KillSwitchIcon,
  Medal as QualityIcon,
  Settings as SettingsIcon,
  CreditCard as BillingIcon,
  HelpCircle as DocsIcon,
  Menu as MenuIcon,
  Xmark as XmarkIcon,
  NavArrowLeft as CollapseIcon,
  SunLight as SunIcon,
  HalfMoon as MoonIcon,
  LogOut as SignOutIcon,
  OpenNewWindow as ExternalIcon,
  CheckCircle as ApprovalsIcon,
  EditPencil as DraftsIcon,
  SendDiagonal as ReleaseIcon,
  NavArrowDown as ChevronIcon,
} from 'iconoir-react'
import { cn } from '@/lib/utils'
import { UserButton } from '@clerk/react'
import { useAuth } from '@/hooks/useAuth'
import { useSelectedWorkspace } from '@/hooks/useSelectedWorkspace'
import { useRole } from '@/hooks/useRole'
import { useTheme } from '@/hooks/useTheme'
import { AlertsDropdown } from '@/components/AlertsDropdown'
import { CommandPalette } from '@/components/ui/CommandPalette'
import { HIDE_LEGACY_PAGES, isHidden } from '@/config/legacy-pages'
import { getFilteredNavGroups, flattenNavItems, activeNavPath, countFor, navCountsFor } from '@/config/nav-groups'
import WorkspaceSwitcher from '@/components/engine/access/WorkspaceSwitcher'
import { StatusStrip } from './StatusStrip'
import { ThemePicker } from './ThemePicker'
import { heldStoreFor } from '@/components/engine/unmatched/held-store'

const SIDEBAR_COLLAPSED_KEY = 'clairvos-admin-sidebar-collapsed'

const ICONS = {
  dashboard: DashboardIcon,
  clients: ClientsIcon,
  playbooks: PlaybooksIcon,
  leadDiscovery: LeadDiscoveryIcon,
  campaigns: CampaignsIcon,
  email: EmailIcon,
  blockList: BlockListIcon,
  replies: RepliesIcon,
  analytics: AnalyticsIcon,
  costs: CostsIcon,
  reports: ReportsIcon,
  studio: StudioIcon,
  portal: PortalIcon,
  activity: ActivityIcon,
  quality: QualityIcon,
  killSwitch: KillSwitchIcon,
  settings: SettingsIcon,
  billing: BillingIcon,
  docs: DocsIcon,
  external: ExternalIcon,
  approvals: ApprovalsIcon,
  drafts: DraftsIcon,
  release: ReleaseIcon,
}

// The menu is data (src/config/nav-groups.js). Environment settings supply the outside links.
const navGroupsFor = role => getFilteredNavGroups(role, { env: import.meta.env })

// Flatten nav items for slider calculation
const ALL_NAV_ITEMS = flattenNavItems()

// Badge numbers for menu entries that ask for one, from the held reply list the status strip also reads (one shared request).
function useNavCounts() {
  const { getToken, user, sessionId, isAuthenticated } = useAuth()
  const workspaceId = useSelectedWorkspace()
  // The answer is kept with the workspace it was read for, so a switch shows no count until the new read lands.
  const [slot, setSlot] = useState({ workspaceId: null, held: null })
  const setHeld = held => setSlot({ workspaceId, held })
  const held = slot.workspaceId === workspaceId ? slot.held : null
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_ENGINE_API_URL
    if (!isAuthenticated || !user?.id || !sessionId || !baseUrl) { setHeld(null); return undefined }
    let live = true
    const store = heldStoreFor({ baseUrl, getToken, userId: user.id, sessionId, allowLocal: import.meta.env.DEV, workspaceId })
    store.read().then(next => { if (live) setHeld(next) })
    const unsubscribe = store.subscribe(() => { const next = store.peek(); if (live && next) setHeld(next) })
    return () => { live = false; unsubscribe() }
  }, [getToken, user?.id, sessionId, isAuthenticated, workspaceId])
  return useMemo(() => navCountsFor(held), [held])
}

function NavItem({ item, collapsed, onClick, itemRef, counts }) {
  const Icon = ICONS[item.icon] || ExternalIcon
  const child = Boolean(item.parent)
  const count = countFor(item, counts)
  const className = isActive =>
    cn(
      'group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium z-10',
      'transition-colors duration-100',
      isActive
        ? 'text-(--accent)'
        : 'text-(--muted-foreground) hover:text-(--accent)',
      child && !collapsed && 'pl-[34px] text-[13.5px]',
      collapsed && 'justify-center px-2'
    )
  const body = (
    <>
      {(!child || collapsed) && <Icon width={18} height={18} strokeWidth={1.5} className="shrink-0" />}
      {!collapsed && <span className="flex-1 min-w-0 truncate">{item.label}</span>}
      {!collapsed && item.href && <ExternalIcon width={12} height={12} strokeWidth={1.5} className="shrink-0" aria-hidden="true" />}
      {!collapsed && count && (
        <span className="ml-auto px-1.5 rounded-full bg-(--accent) text-(--accent-foreground) text-[10.5px] font-medium">{count}</span>
      )}
    </>
  )

  // An outside link: opens in a new tab, never part of the sliding highlight.
  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={className(false)}
        title={collapsed ? item.label : undefined}
      >
        {body}
      </a>
    )
  }

  return (
    <NavLink
      ref={itemRef}
      to={item.path}
      onClick={onClick}
      className={({ isActive }) => className(isActive)}
      title={collapsed ? item.label : undefined}
    >
      {body}
    </NavLink>
  )
}

// One menu entry and its indented children.
function NavEntry({ item, collapsed, onClick, itemRefs, counts }) {
  return (
    <>
      <NavItem item={item} collapsed={collapsed} onClick={onClick} counts={counts} itemRef={itemRefs && (el => { if (item.path) itemRefs.current[item.path] = el })} />
      {(item.children || []).map(child => (
        <NavItem key={child.path} item={{ ...child, parent: item.path }} collapsed={collapsed} onClick={onClick} counts={counts} itemRef={itemRefs && (el => { itemRefs.current[child.path] = el })} />
      ))}
    </>
  )
}

// A menu group. A collapsible group is drawn closed until opened, and starts open when the current page is in it.
function NavGroup({ group, groupIndex, collapsed, onClick, itemRefs, counts, pathname }) {
  const holdsCurrentPage = Boolean(activeNavPath(pathname, flattenNavItems([group])))
  const [openByHand, setOpenByHand] = useState(false)
  const open = !group.collapsible || openByHand || holdsCurrentPage
  const id = `nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  const toggle = () => setOpenByHand(prev => !prev)
  return (
    <div className="space-y-1" data-nav-group={group.label} data-open={open}>
      {group.collapsible ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={id}
          title={collapsed ? group.label : undefined}
          className={cn(
            'flex w-full items-center gap-2 px-3 mb-2 text-[10px] font-semibold text-(--muted-foreground) tracking-widest uppercase',
            'hover:text-(--accent) transition-colors duration-100',
            collapsed && 'justify-center px-2'
          )}
        >
          <ChevronIcon width={12} height={12} strokeWidth={2} className={cn('shrink-0 transition-transform duration-150', !open && '-rotate-90')} aria-hidden="true" />
          {!collapsed && <span>{group.label}</span>}
        </button>
      ) : !collapsed ? (
        <h3 className="px-3 mb-2 text-[10px] font-semibold text-(--muted-foreground) tracking-widest uppercase">{group.label}</h3>
      ) : groupIndex > 0 ? (
        <div className="h-px bg-(--border) mx-2 my-3" />
      ) : null}
      {open && (
        <div id={id} className="space-y-0.5">
          {group.items.map(item => (
            <NavEntry key={item.path || item.label} item={item} collapsed={collapsed} onClick={onClick} itemRefs={itemRefs} counts={counts} />
          ))}
        </div>
      )}
    </div>
  )
}

function Sidebar({ collapsed, onToggle, onLogout }) {
  const location = useLocation()
  const { role } = useRole()
  const navCounts = useNavCounts()
  const navRef = useRef(null)
  const itemRefs = useRef({})
  const [sliderStyle, setSliderStyle] = useState({ top: 0, height: 0, opacity: 0 })
  const [mounted, setMounted] = useState(false)

  // Filter nav based on role
  const filteredNavGroups = useMemo(() => navGroupsFor(role), [role])
  
  // Find active path: an exact match wins, then the longest parent path (nested routes)
  const activePath = useMemo(() => activeNavPath(location.pathname, ALL_NAV_ITEMS), [location.pathname])
  
  // Set mounted after first render to allow refs to populate
  useEffect(() => {
    setMounted(true)
  }, [])
  
  // Update slider position
  const updateSlider = useCallback(() => {
    if (!navRef.current || !activePath || !mounted) {
      setSliderStyle(prev => ({ ...prev, opacity: 0 }))
      return
    }
    
    const activeEl = itemRefs.current[activePath]
    if (activeEl) {
      const navRect = navRef.current.getBoundingClientRect()
      const itemRect = activeEl.getBoundingClientRect()
      
      // Account for scroll offset
      const scrollTop = navRef.current.scrollTop || 0
      
      setSliderStyle({
        top: itemRect.top - navRect.top + scrollTop,
        height: itemRect.height,
        opacity: 1,
      })
    }
  }, [activePath, mounted])
  
  // Update slider on route change, collapse toggle, or mount
  useEffect(() => {
    // Immediate update
    updateSlider()
    // Delayed update to catch any layout shifts
    const timeout1 = setTimeout(updateSlider, 50)
    const timeout2 = setTimeout(updateSlider, 150)
    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [updateSlider, location.pathname, collapsed])
  
  // Update slider on window resize
  useEffect(() => {
    window.addEventListener('resize', updateSlider)
    return () => window.removeEventListener('resize', updateSlider)
  }, [updateSlider])
  
  // Update slider on nav scroll
  useEffect(() => {
    const navEl = navRef.current
    if (!navEl) return
    
    navEl.addEventListener('scroll', updateSlider)
    return () => navEl.removeEventListener('scroll', updateSlider)
  }, [updateSlider])
  
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col',
        'bg-(--sidebar-bg) border-r border-(--border)',
        'transition-all duration-200',
        collapsed ? 'w-[68px]' : 'w-60'
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-center px-2 py-4 border-b border-(--border)">
        <svg viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 shrink-0">
          <ellipse cx="64" cy="64" rx="52" ry="24" transform="rotate(-30 64 64)" stroke="var(--accent)" strokeWidth="8" strokeLinecap="round"/>
          <ellipse cx="64" cy="64" rx="52" ry="24" transform="rotate(30 64 64)" stroke="var(--accent)" strokeWidth="8" strokeLinecap="round" opacity="0.6"/>
          <circle cx="64" cy="64" r="10" fill="var(--accent)"/>
        </svg>
      </div>
      
      {/* Navigation with Liquid Glass Slider - scrollbar hidden */}
      <nav ref={navRef} className="flex-1 overflow-y-auto p-3 space-y-6 relative scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {/* Liquid Glass Sliding Pill */}
        <motion.div
          className="absolute left-3 right-3 rounded-lg pointer-events-none"
          initial={false}
          animate={{
            top: sliderStyle.top,
            height: sliderStyle.height,
            opacity: sliderStyle.opacity,
          }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
            mass: 0.6,
          }}
          style={{
            background: 'linear-gradient(135deg, var(--accent-a-12) 0%, var(--accent-a-08) 100%)',
            backdropFilter: 'blur(8px)',
            border: '1px solid var(--accent-a-20)',
            boxShadow: '0 4px 20px var(--accent-a-10), inset 0 1px 0 var(--highlight-a-10)',
          }}
        />
        
        {filteredNavGroups.map((group, groupIndex) => (
          <NavGroup
            key={group.label}
            group={group}
            groupIndex={groupIndex}
            collapsed={collapsed}
            itemRefs={itemRefs}
            counts={navCounts}
            pathname={location.pathname}
          />
        ))}
      </nav>
      
      {/* Footer */}
      <div className="p-3 border-t border-(--border) space-y-1">
        {/* System Status */}
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg bg-(--warning)/10',
          collapsed && 'justify-center px-2'
        )}>
          <span className="relative flex h-2 w-2">
            {/* No ping animation — status is unknown, will be wired to real health checks */}
            <span className="relative inline-flex rounded-full h-2 w-2 bg-(--warning)"></span>
          </span>
          {!collapsed && (
            <span className="text-xs font-medium text-(--warning)">Status Unknown</span>
          )}
        </div>
        
        {/* Collapse button */}
        <button
          onClick={onToggle}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm',
            'text-(--muted-foreground) hover:text-(--foreground) hover:bg-(--muted)',
            'transition-colors duration-100',
            collapsed && 'justify-center px-2'
          )}
        >
          <CollapseIcon width={16} height={16} strokeWidth={1.5} className={cn('transition-transform duration-150', collapsed && 'rotate-180')} />
          {!collapsed && <span>Collapse</span>}
        </button>
        
        {/* Logout button */}
        <button
          onClick={onLogout}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm',
            'text-(--error) hover:bg-(--error)/10',
            'transition-colors duration-100',
            collapsed && 'justify-center px-2'
          )}
        >
          <SignOutIcon width={16} height={16} strokeWidth={1.5} />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  )
}

function MobileNav({ open, onClose, onLogout }) {
  const location = useLocation()
  const { role } = useRole()
  const navCounts = useNavCounts()
  const filteredNavGroups = useMemo(() => navGroupsFor(role), [role])
  
  useEffect(() => {
    if (open) onClose()
  }, [location.pathname])
  
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])
  
  if (!open) return null
  
  return (
    <>
      <div 
        className="fixed inset-0 z-40 bg-(--scrim-50) backdrop-blur-xs lg:hidden"
        onClick={onClose}
      />
      
      <aside className="fixed inset-y-0 left-0 z-50 w-72 bg-(--sidebar-bg) border-r border-(--border) lg:hidden">
        <div className="flex items-center justify-end h-16 px-4">
          <button
            onClick={onClose}
            className="p-2 text-(--muted-foreground) hover:text-(--foreground) rounded-lg hover:bg-(--muted)"
          >
            <XmarkIcon width={20} height={20} strokeWidth={1.5} />
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {filteredNavGroups.map((group, groupIndex) => (
            <NavGroup key={group.label} group={group} groupIndex={groupIndex} collapsed={false} onClick={onClose} counts={navCounts} pathname={location.pathname} />
          ))}
        </nav>
        
        <div className="p-4 border-t border-(--border) space-y-1">
          <div className="px-3 pb-2 space-y-2">
            <WorkspaceLabel />
            <StatusStrip />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-(--warning)/10">
            <span className="relative flex h-2 w-2">
              {/* No ping animation — status is unknown, will be wired to real health checks */}
              <span className="relative inline-flex rounded-full h-2 w-2 bg-(--warning)"></span>
            </span>
            <span className="text-xs font-medium text-(--warning)">Status Unknown</span>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-(--error) hover:bg-(--error)/10"
          >
            <SignOutIcon width={16} height={16} strokeWidth={1.5} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  )
}

// Top header bar with action buttons
// Role badge color mapping
const ROLE_BADGE_STYLES = {
  admin: 'bg-(--accent)/15 text-(--accent) border-(--accent)/30',
  operator: 'bg-(--info)/15 text-(--info) border-(--info)/30',
  viewer: 'bg-(--muted-foreground)/15 text-(--muted-foreground) border-(--muted-foreground)/30',
}

// Which workspace this console is working in. The same pill for everyone; only an owner with more
// than one workspace gets a switch inside it (components/engine/access/WorkspaceSwitcher.jsx).
function WorkspaceLabel() {
  return <WorkspaceSwitcher />
}

function TopHeader({ onMenuClick, sidebarCollapsed }) {
  const { theme, toggleTheme } = useTheme()
  const { role } = useRole()
  const { user } = useAuth()
  
  return (
    <header className={cn(
      'fixed top-0 right-0 z-40 h-14',
      'bg-linear-to-r from-(--muted)/40 via-(--muted)/50 to-(--muted)/40',
      'backdrop-blur-xs',
      'flex items-center justify-end px-6 gap-4',
      'transition-all duration-200',
      sidebarCollapsed ? 'lg:left-[68px]' : 'lg:left-60',
      'left-0'
    )}>
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="p-2 text-(--muted-foreground) hover:text-(--accent) transition-colors duration-200 lg:hidden mr-auto"
      >
        <MenuIcon width={20} height={20} strokeWidth={1.5} />
      </button>
      
      {/* Left of the desktop bar: workspace label and status strip */}
      <div className="flex-1 hidden lg:flex items-center self-stretch gap-3 min-w-0 overflow-hidden">
        <WorkspaceLabel />
        <StatusStrip inline />
      </div>

      {/* Role badge + Clerk user button (profile, MFA, sessions) */}
      <div className="hidden sm:flex items-center gap-2">
        <span className={cn(
          'px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border',
          ROLE_BADGE_STYLES[role] || ROLE_BADGE_STYLES.viewer
        )}>
          {role}
        </span>
        <UserButton
          appearance={{
            variables: { colorPrimary: '#864AEC' },
            elements: {
              avatarBox: 'w-7 h-7',
              userButtonPopoverCard: 'shadow-xl border border-(--border)',
            }
          }}
          afterSignOutUrl="/"
        />
      </div>
      
      {/* Alerts dropdown: legacy admin API, hidden with the legacy pages */}
      {!HIDE_LEGACY_PAGES && <AlertsDropdown />}
      
      {/* Theme menu, then the light and dark toggle */}
      <ThemePicker />
      <button
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        className="p-2 text-(--muted-foreground) hover:text-(--accent) transition-colors duration-200"
      >
        {theme === 'dark' ? (
          <SunIcon width={20} height={20} strokeWidth={1.5} className="animate-theme-icon" key="sun" />
        ) : (
          <MoonIcon width={20} height={20} strokeWidth={1.5} className="animate-theme-icon" key="moon" />
        )}
      </button>
      
      {/* Settings: a legacy admin API page, hidden with the legacy pages */}
      {!isHidden('/settings') && (
        <Link
          to="/settings"
          title="Settings"
          className="p-2 text-(--muted-foreground) hover:text-(--accent) transition-colors duration-200"
        >
          <SettingsIcon width={20} height={20} strokeWidth={1.5} />
        </Link>
      )}
    </header>
  )
}

export function Shell({ children }) {
  const { logout } = useAuth()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    }
    return false
  })
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  
  const handleSidebarToggle = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newState))
  }
  
  return (
    <div className="min-h-screen bg-(--background) flex flex-col">
      {/* Command palette (Cmd+K) */}
      <CommandPalette />
      
      <div className="hidden lg:block">
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={handleSidebarToggle}
          onLogout={logout}
        />
      </div>
      
      <MobileNav 
        open={mobileNavOpen} 
        onClose={() => setMobileNavOpen(false)}
        onLogout={logout}
      />
      
      {/* Top header bar */}
      <TopHeader 
        onMenuClick={() => setMobileNavOpen(true)} 
        sidebarCollapsed={sidebarCollapsed}
      />
      
      <div className={cn(
        'flex flex-col flex-1 transition-all duration-200',
        sidebarCollapsed ? 'lg:ml-[68px]' : 'lg:ml-60'
      )}>
        {/* Content with top padding for header */}
        <main className="flex-1 p-4 pt-20 lg:p-6 lg:pt-20">{children}</main>
        <footer className="px-4 py-3 text-center text-xs text-(--muted-foreground) lg:px-6">
          Powered by Clairvos
        </footer>
      </div>
    </div>
  )
}

export function PageWrapper({ title, description, icon: Icon, actions, narrow = false, className, children }) {
  return (
    <motion.div 
      className={cn('space-y-6', className)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
    >
      {(title || description || actions) && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pl-5">
          <div>
            {title && <h1 className="text-2xl font-semibold text-(--foreground)">{title}</h1>}
            {description && <p className="mt-0.5 text-sm text-(--muted-foreground)">{description}</p>}
          </div>
          {actions && (
            <div className="flex items-center gap-3">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={cn(narrow && 'max-w-4xl')}>{children}</div>
    </motion.div>
  )
}
